import { useApprovalStampOnlyAccess } from './useApprovalStampOnlyAccess';
import { useButtonPermissions } from './useButtonPermissions';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useOptionalLocationTagsDraft } from '../contexts/LocationTagsDraftContext';
import { normalizeStage } from '../types/stages';
import {
  isExplicitDraftLocationTagRequestId,
  isPersistedServerRequestId,
} from '../utils/locationTagRequestId';

/** Single client-side authority used by both tag management and inline creation. */
export function useLocationTagMutationAuthority(
  requestId?: string | null,
  requestStage?: string,
  requestorEmail?: string | null,
): { canMutate: boolean; isLoading: boolean } {
  const { isImpersonating, mode } = useImpersonation();
  const normalizedStage = requestStage ? normalizeStage(requestStage) : null;
  const { approverMutationsBlocked } = useApprovalStampOnlyAccess({
    requestorEmail,
    requestStage: normalizedStage,
  });
  const draftContext = useOptionalLocationTagsDraft();
  const isServerMode = isPersistedServerRequestId(requestId);
  const isDraftMode =
    draftContext != null && isExplicitDraftLocationTagRequestId(requestId);
  const { canShowSave, isLoading } = useButtonPermissions(
    requestStage,
    isServerMode ? requestId ?? undefined : undefined,
  );

  if (isImpersonating && mode === 'read_only') {
    return { canMutate: false, isLoading };
  }
  if (approverMutationsBlocked) {
    return { canMutate: false, isLoading };
  }
  if (isDraftMode) {
    return { canMutate: true, isLoading: false };
  }
  if (!isServerMode || isLoading || !canShowSave) {
    return { canMutate: false, isLoading };
  }

  return { canMutate: true, isLoading: false };
}
